import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.sass']
})
/**
 * This component currently does the following:
 * - Accepts user to select file/files to upload
 * - Accepts user dragging file/files to upload
 * - Emits out the files selected on either drag/upload, as FileList or Binary (string/ArrayBuffer)
 */
export class FileUploadComponent implements OnInit {

  @ViewChild('fileBrowser') fileBrowser: ElementRef
  @ViewChild('dragArea') dragArea: ElementRef

  /** 
   * Changes the output to Binary/ArrayBuffer
   * Defaults to emit FileList
   */
  @Input('getBinary') getBinary: boolean = false
  /** 
   * Defaults display title
   */
  @Input('title') title: string = 'Drag file(s) or browse from computer'
  /**
   * Types of file to be accepted
   * If empty, it will accept all file type
   */
  @Input('types') types: 'texts' | 'images' | 'videos' | 'audios' | 'custom'

  /**
   * Custom type for @var types is 'custom'
   * Will throw an error if empty when @var types is 'custom' 
   * Refer: https://www.w3schools.com/tags/att_input_accept.asp
   */
  @Input('customFileTypes') customType: string

  @Input('isSingle') isSingle: boolean = false

  @Input('isDisable') isDisable: boolean = false

  /** UI variable for FileUploadComponent */
  ui: {
    /**
     * Upload types
     * TODO: To check for type of file during drag event
     */
    uploadTypes?: string,
    /** 
     * Single or multiple file upload
     * TODO: To check number of files when single during drag event
     */
    isSingle?: boolean
    /** Toggles between file selector or folder selector */
    isFolder?: boolean
    /** State of file/folder browser button hovering */
    isHover?: boolean
  } = {
    isFolder: false,
    isHover: false
  }


  /** State of user dragging */
  isDragging: boolean

  /** 
   * Emits out data after files has been selected for upload 
   * if !getBinary = FileList
   * if getBinary = string | ArrayBuffer
   */
  @Output() onSelected: EventEmitter<FileList | string | ArrayBuffer> = new EventEmitter()

  constructor(
    private fileUploadEle: ElementRef,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.processInput()
  }

  /** Change event mitted on user upload files */
  async processFileList(files: FileList) {
    if (this.isDisable) return;
    try {
      if (this.getBinary) {
        const file = await this.fileListToBinary(files)
        this.onSelected.emit(file)
      }
      else
        this.onSelected.emit(files)

      /** This ensures that the <input type=file> is always reset upon upload */
      this.fileBrowser.nativeElement.value = ''
    }
    catch (err) {
      console.error('processFileList().err: ', err)
    }
  }

  /** 
   * Transform SINGLE FileList item into binary
   */
  fileListToBinary(files: FileList): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader()

        reader.addEventListener('load', (e) => {
          const file = e?.target?.result

          /** Read through the file here */
          resolve(file)
        })
        reader.readAsBinaryString(files.item(0))
      }
      catch (err) {
        reject(err)
      }
    })
  }

  /** Emits dragevent based on the name */
  async dragEvents(name: string, evt: any) {
    function parseFileEntry(fileEntry) {
      return new Promise((resolve, reject) => {
        fileEntry.file(
          file => {
            resolve(file);
          },
          err => {
            reject(err);
          }
        );
      });
    }

    function parseDirectoryEntry(directoryEntry): Promise<any[]> {
      const directoryReader = directoryEntry.createReader();
      return new Promise((resolve, reject) => {
        directoryReader.readEntries(
          entries => {
            resolve(entries);
          },
          err => {
            reject(err);
          }
        );
      });
    }

    const addClass = (name: string) => {
      this.dragArea.nativeElement.classList.add(name)
    }
    const removeClass = (name: string) => {
      this.dragArea.nativeElement.classList.remove(name)
    }
    const dragging = (dragging: boolean) => {
      if (dragging) {
        removeClass('not-dragging')
        addClass('dragging')
        this.isDragging = true
      }
      else {
        removeClass('dragging')
        addClass('not-dragging')
        this.isDragging = false
      }
    }

    switch (name) {
      case 'enter': {
        dragging(true)
      } break
      case 'leave': {
        dragging(false)
      } break
      case 'over': {
        // Will need to do data validation here, ensuring that the file has the right type
      } break
      case 'drop': {
        dragging(false)

        if (!evt?.dataTransfer?.items?.length) break

        const items = evt.dataTransfer.items
          , itemsLength = items.length

        const entries = []
        for (let i = 0; i < itemsLength; i++) {
          const item = items[i]
          const entry = item.webkitGetAsEntry()
          entries.push(entry)
        }

        let files = []

        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i]

          if (entry?.isFile) {
            const file = await parseFileEntry(entry)
            files.push(file)
          }
          else if (entry?.isDirectory) {
            const directory = await parseDirectoryEntry(entry)
            
            for (let dIdx = 0; dIdx < directory.length; dIdx++) {
              const file = await parseFileEntry(directory[dIdx])

              files.push(file)
            }
          }
        }

        const dt = new DataTransfer()
        files.map(file => {
          dt.items.add(file)
        })
        const fileList = dt.files

        this.processFileList(fileList)
      } break
    }
    evt.preventDefault()
    evt.stopPropagation()
  }

  /** Processes the input when init */
  processInput() {
    /** Processes this.types input */
    switch (this.types) {
      case 'audios': { this.ui.uploadTypes = 'audio/*' } break
      case 'videos': { this.ui.uploadTypes = 'video/*' } break
      case 'images': { this.ui.uploadTypes = 'image/*' } break
      case 'texts': { this.ui.uploadTypes = 'text/*' } break
      case 'custom': {
        if (!this.customType) {
          throw new Error(`FileUploadComponent requires input customType when input type is set to 'custom'`)
        }
        this.ui.uploadTypes = this.customType
      } break
      default: {
        this.ui.uploadTypes = ''
      }
    }

    /** Processes isSingle input */
    this.ui.isSingle = this.isSingle
  }

  eleShiftKeydown: () => void
  eleShiftKeyup: () => void

  onSelectHover(state: boolean) {
    if (state) {
      this.ui.isHover = true
      this.eleShiftKeydown = this.renderer.listen('document', 'keydown', (event: any) => {
        if (event.shiftKey || event.key === 'Shift')
          this.ui.isFolder = true
      })
      this.eleShiftKeyup = this.renderer.listen('document', 'keyup', (event: any) => {
        
        if (event.shiftKey || event.key === 'Shift') {
          this.ui.isFolder = false
        }
      })
    }
    else {
      this.ui.isHover = false
      this.ui.isFolder = false

      this.eleShiftKeydown()
      this.eleShiftKeyup()
    }
  }
}